import {
  AnnotationIcon,
  BellIcon,
  BookOpenIcon,
  CalendarIcon,
  ChatAlt2Icon,
  CheckCircleIcon,
  CreditCardIcon,
  GlobeAltIcon,
  IdentificationIcon,
  KeyIcon,
  LightningBoltIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import ConversationsInbox from '../mocks/ConversationsInbox';
import LineChat from '../mocks/LineChat';
import LineSetup from '../mocks/LineSetup';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Sakura Ryokan',
  initial: 'S',
  bg: 'bg-[#be123c]',
};

/*
 * Facts this page relies on (checked 2026-10-21): LINE is not in
 * FREE_PLAN_CHANNELS; connecting checks the long-lived token with
 * /v2/bot/info and registers the webhook itself; the bots server answers
 * text in 1:1 chats and in groups/rooms (every text message, no mention
 * needed), replies "I can only read text" to anything else, shows LINE's
 * loading animation in 1:1 chats, and sends with the reply token first,
 * falling back to push (which counts against the account's quota)
 * (web-app routes/.../line.handler.ts, utils/line.ts; docs/agents/line.mdx).
 */
export function useLineContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'line',
    name: 'LINE',
    logo: 'FaLine',
    seoTitle: t('LINE Chatbot: An AI Agent for Your LINE Official Account'),
    seoDescription: t(
      'Connect an AI agent to your LINE Official Account. It answers customers 24/7 from your own knowledge and books appointments, and the webhook is set up for you.',
    ),
    hero: {
      label: t('LINE AI chatbot'),
      title: t('Your LINE Official Account, always answering.'),
      accent: t('In your customers’ language.'),
      lead: t(
        'Paste two keys from the LINE Developers console and your AI agent answers every chat from your website and documents. It can book appointments too, while you sleep.',
      ),
      facts: [
        t('Webhook set up for you'),
        t('Chats and group chats'),
        t('LINE on paid plans'),
      ],
      description: t(
        'Example: a guest asks a ryokan’s LINE Official Account about check-in time, then books the private onsen for the evening. The agent answers from the guest guide and makes the booking.',
      ),
      demo: (
        <LineChat
          brand={BRAND}
          lines={[
            {
              from: 'customer',
              text: t('Hello! What time is check-in?'),
              time: '19:40',
              at: 600,
            },
            {
              from: 'agent',
              text: t(
                'Check-in starts at 15:00 and check-out is by 10:00. We can keep your bags if you arrive early.',
              ),
              time: '19:40',
              typingAt: 1100,
              at: 2100,
            },
            {
              from: 'customer',
              text: t('Can we book the private onsen at 20:00?'),
              time: '19:41',
              at: 3200,
            },
            {
              from: 'agent',
              text: t(
                'Done! The private onsen is reserved for you at 20:00. Enjoy your stay ♨️',
              ),
              time: '19:41',
              typingAt: 3600,
              at: 4500,
            },
          ]}
        />
      ),
      steps: [
        {
          icon: BookOpenIcon,
          title: t('Checked the guest guide'),
          via: tc('Knowledge'),
          at: 1500,
        },
        {
          icon: CheckCircleIcon,
          title: t('Booked the private onsen'),
          via: tc('Google Calendar'),
          at: 3900,
        },
      ],
    },
    jobs: {
      title: t('Every LINE chat, answered.'),
      tail: t('Not just the easy ones.'),
      lead: t(
        'Friends of your account ask about opening hours, prices and bookings at all hours. Your agent answers from what you’ve taught it and takes care of the next step.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Opening hours, prices, services and policies, straight from your website and documents.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books appointments'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: UserGroupIcon,
          title: t('Works in groups'),
          detail: t(
            'Add the account to a group chat and the agent answers there too, with its own history for each chat.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Checks your systems'),
          detail: t(
            'Looks up an order or a booking in your own system with a custom action.',
          ),
        },
      ],
    },
    setup: {
      title: t('Two keys, and you’re live.'),
      tail: t('No webhook to copy.'),
      steps: [
        {
          title: t('Turn on the Messaging API'),
          detail: t(
            'In LINE Official Account Manager, open Settings, then Messaging API, and enable it for your account.',
          ),
        },
        {
          title: t('Copy two keys'),
          detail: t(
            'In the LINE Developers console, copy the channel secret and issue a long-lived channel access token.',
          ),
        },
        {
          title: t('Paste and save'),
          detail: t(
            'Paste both into your agent’s LINE window and click Save. The keys are checked and the webhook is registered with LINE for you.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/line',
      guideLabel: t('Read the LINE setup guide'),
      description: t(
        'The LINE Developers console with the channel secret and the long-lived access token, next to the agent’s LINE window showing both keys checked, the webhook registered and the account connected.',
      ),
      visual: <LineSetup />,
    },
    spotlight: {
      title: t('Read every LINE chat.'),
      tail: t('Right next to your other channels.'),
      lead: t(
        'Every conversation on your LINE account appears in Conversations, with what the customer wrote and what your agent answered, next to your website, WhatsApp, Messenger and Telegram chats.',
      ),
      points: [
        t('One-to-one chats and group chats'),
        t('Each chat keeps its own history'),
        t('Filter by channel in one click'),
        t('See exactly what your agent said'),
      ],
      description: t(
        'The agent’s Conversations page filtered to LINE, listing guests’ chats, with the check-in conversation open.',
      ),
      visual: (
        <ConversationsInbox
          channel='line'
          brand={BRAND}
          threads={[
            {
              name: 'Yuki Tanaka',
              preview: t(
                'Done! The private onsen is reserved for you at 20:00. Enjoy your stay ♨️',
              ),
              time: '19:41',
              avatar: 'bg-[linear-gradient(140deg,#f472b6,#be185d)]',
            },
            {
              name: 'Somchai P.',
              preview: t('Is breakfast included in the room price?'),
              time: '18:05',
              avatar: 'bg-[linear-gradient(140deg,#38bdf8,#1d4ed8)]',
            },
            {
              name: 'Mei Lin',
              preview: t('Do you have a shuttle from the station?'),
              time: '16:22',
              avatar: 'bg-[linear-gradient(140deg,#34d399,#047857)]',
            },
            {
              name: 'Kenji Mori',
              preview: t('Thank you, see you tomorrow!'),
              time: '09:12',
              avatar: 'bg-[linear-gradient(140deg,#fbbf24,#d97706)]',
            },
          ]}
          messages={[
            { from: 'customer', text: t('Hello! What time is check-in?') },
            {
              from: 'agent',
              text: t(
                'Check-in starts at 15:00 and check-out is by 10:00. We can keep your bags if you arrive early.',
              ),
            },
            {
              from: 'customer',
              text: t('Can we book the private onsen at 20:00?'),
            },
            {
              from: 'agent',
              text: t(
                'Done! The private onsen is reserved for you at 20:00. Enjoy your stay ♨️',
              ),
            },
          ]}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('LINE, in plain words.'),
      items: [
        {
          icon: CreditCardIcon,
          title: t('Paid plans'),
          detail: t(
            'LINE is on paid plans, and you can build and test your agent for free first. You need a LINE Official Account with the Messaging API turned on.',
          ),
        },
        {
          icon: AnnotationIcon,
          title: t('Text messages'),
          detail: t(
            'Your agent reads text. When someone sends a sticker, a photo or anything else, it replies that it can only read text.',
          ),
        },
        {
          icon: UserGroupIcon,
          title: t('Groups answer every message'),
          detail: t(
            'In a group the account has been added to, the agent replies to every text message, so add it to groups where that’s what you want.',
          ),
        },
        {
          icon: PaperAirplaneIcon,
          title: t('Reply messages first'),
          detail: t(
            'Answers go out as LINE reply messages. If one takes too long, it’s sent as a push message instead, which counts toward your LINE message quota.',
          ),
        },
        {
          icon: BellIcon,
          title: t('Turn off auto-replies'),
          detail: t(
            'Switch off the account’s greeting and auto-reply messages in LINE Official Account Manager, so they don’t answer alongside your agent.',
          ),
        },
        {
          icon: KeyIcon,
          title: t('One account per agent'),
          detail: t(
            'Each LINE Official Account connects to one agent. Pause it at any time with one switch.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('Can I use my existing LINE Official Account?'),
        answer: t(
          'Yes. Turn on the Messaging API in LINE Official Account Manager, then copy the channel secret and a long-lived channel access token from the LINE Developers console into your agent.',
        ),
      },
      {
        question: t('Do I need to set up a webhook?'),
        answer: t(
          'No. When you save the keys, they’re checked and the webhook is registered with LINE for you. Just make sure Use webhook is on in the LINE Developers console.',
        ),
      },
      {
        question: t('Which plan do I need?'),
        answer: t(
          'LINE is on paid plans. You can build and test your agent on the Free plan first.',
        ),
      },
      {
        question: t('Does it work in group chats?'),
        answer: t(
          'Yes, in groups the account has been added to. It replies to every text message there, and each chat keeps its own history.',
        ),
      },
      {
        question: t('Can it read stickers and photos?'),
        answer: t(
          'No. It reads text messages, and replies that it can only read text when someone sends anything else.',
        ),
      },
      {
        question: t('Do replies count toward my LINE message quota?'),
        answer: t(
          'Answers are sent as LINE reply messages. If a reply takes too long, it goes out as a push message instead, and push messages count toward your quota.',
        ),
      },
      {
        question: t('Where do I read the conversations?'),
        answer: t(
          'In Conversations, under LINE, next to your website, WhatsApp, Messenger and Telegram chats.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each customer writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your LINE account could be answering by lunchtime.'),
    finalExchange: {
      question: t('Can I check in early tomorrow?'),
      answer: t('From 13:00 if your room is ready. Shall I note it for you?'),
    },
  };
}
